import { AnimatePresence } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import Modal from '@/components/Modal'
import type { Product } from '@/lib/api.types'

import ProductDescriptionPreview from '../ProductDescriptionPreview'
import ProductModal from '../ProductModal'
import TimeSlot from '../TimeSlot'
import {
  AvailabilityModalContent,
  AvailabilityModalDivider,
  AvailabilityModalFooter,
  AvailabilityModalHero,
  AvailabilityModalInfo,
  AvailabilityModalMedia,
  AvailabilityModalSelection,
  AvailabilityModalSlots,
  AvailabilityModalSlotsSection,
  AvailabilityModalSlotsTitle
} from './AvailabilityModal.styles'

interface AvailabilitySlot {
  time: string
  availability: number
  language?: string | null
}

interface AvailabilityModalProps {
  product: Product
  slots: AvailabilitySlot[]
  selectedTime: string | null
  onSelect: (hour: string) => void
  onClose: () => void
}

const AvailabilityModal = ({ product, slots, selectedTime, onSelect, onClose }: AvailabilityModalProps) => {
  const { t } = useTranslation()
  const [pendingHour, setPendingHour] = useState<string | null>(selectedTime ?? null)
  const [showProductModal, setShowProductModal] = useState(false)
  const title = product.name
  const coverImageUrl = product.coverImage?.url || product.images?.[0]?.url || '/img/gallery01.jpg'

  useEffect(() => {
    setPendingHour(selectedTime ?? null)
  }, [selectedTime])

  const handleSelect = () => {
    if (!pendingHour) {
      return
    }

    onSelect(pendingHour)
    onClose()
  }

  return (
    <>
      <Modal
        onClose={onClose}
        variant="full"
        showClose={true}
        footer={
          <AvailabilityModalFooter>
            <Button variant="secondary" onClick={onClose}>
              {t('cancel')}
            </Button>
            <AvailabilityModalSelection>
              <span>{t('selectedTime')}:</span>
              <strong>{pendingHour || '--:--'}</strong>
            </AvailabilityModalSelection>
            <Button onClick={handleSelect} disabled={!pendingHour}>
              {t('select')}
            </Button>
          </AvailabilityModalFooter>
        }
      >
        <AvailabilityModalContent>
          <AvailabilityModalHero>
            <AvailabilityModalMedia>
              <img src={coverImageUrl} alt={title} />
            </AvailabilityModalMedia>
            <AvailabilityModalInfo>
              <h3>{title}</h3>
              <ProductDescriptionPreview className="description-preview" product={product} onMore={() => setShowProductModal(true)} />
            </AvailabilityModalInfo>
          </AvailabilityModalHero>

          <AvailabilityModalDivider />

          <AvailabilityModalSlotsSection>
            <AvailabilityModalSlotsTitle>{t('selectVisitTime')}</AvailabilityModalSlotsTitle>
            <AvailabilityModalSlots>
              {slots.map(slot => (
                <TimeSlot
                  key={`${slot.time}-${slot.language ?? 'default'}`}
                  time={slot.time}
                  availability={slot.availability}
                  language={slot.language}
                  active={pendingHour === slot.time}
                  onClick={() => setPendingHour(slot.time)}
                />
              ))}
            </AvailabilityModalSlots>
          </AvailabilityModalSlotsSection>
        </AvailabilityModalContent>
      </Modal>
      <AnimatePresence>{showProductModal && <ProductModal product={product} onClose={() => setShowProductModal(false)} />}</AnimatePresence>
    </>
  )
}

export default AvailabilityModal
